'use client'

import type { Entry } from '@/document'
import { cn } from '@/lib/tailwind'
import { VinylRecordIcon, GlobeIcon } from '@phosphor-icons/react'
import Image from 'next/image'

interface LabelCardProps {
  label: Entry
  className?: string
}

export function LabelCard({ label, className }: LabelCardProps) {
  const metadata = label.metadata as {
    imageUrl?: string
    website?: string
    profile?: string
  }
  const hasImage = !!label.imageUrl

  return (
    <div
      className={cn(
        'group hover:border-foreground/20 relative overflow-hidden rounded-lg border transition-colors',
        className,
      )}
    >
      {/* Image / Placeholder */}
      <div className="bg-muted relative aspect-square overflow-hidden">
        {hasImage ? (
          <Image
            src={label.imageUrl!}
            alt={label.title}
            fill
            className="object-cover transition-transform group-hover:scale-105"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <VinylRecordIcon className="text-muted-foreground/30 h-12 w-12" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="space-y-0.5 p-2.5">
        <h3 className="line-clamp-2 text-sm leading-tight font-medium">
          {label.title}
        </h3>
        {metadata.website && (
          <a
            href={metadata.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-[11px]"
            onClick={(e) => e.stopPropagation()}
          >
            <GlobeIcon className="h-2.5 w-2.5" />
            Website
          </a>
        )}
        {metadata.profile && (
          <p className="text-muted-foreground/60 line-clamp-2 text-[11px] leading-snug">
            {metadata.profile}
          </p>
        )}
      </div>
    </div>
  )
}
